import { ref, computed, watch, Ref } from 'vue';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { useTaskDefinitionsStore } from '@agyletime/vue-3-common/src/composables/taskDefinitionsStore';
import { fetchAssignmentsByWorkloads } from '@/pages/OperationsInsights/services/fetchAssignmentsByWorkloads';
import {
    getTotalAssignmentsByWorkloadColumnDefs,
    tfGdAssignmentTotalByWorkload,
} from '@/pages/OperationsInsights/composables/transformations/tfGdAssignmentTotalByWorkload';
import { EnrichedAssignmentMetricsByWorkload } from '@/pages/OperationsInsights/types/AssignmentMetricsResponseEnriched';
import { tfCsAssignmentOpenByWorkload } from '@/pages/OperationsInsights/composables/transformations/tfCsAssignmentOpenByWorkload';
import { roundToOneDecimal } from '@/pages/OperationsInsights/utils/roundNumber';

import { tfCsAssignmentCompletionTimeByWorkload } from '@/pages/OperationsInsights/composables/transformations/tfCsAssignmentCompletionTimeByWorkload';
import {
    getAssignmentHealthByWorkloadColumnDefs,
    tfGdAssignmentHealthByWorkload,
} from '@/pages/OperationsInsights/composables/transformations/tfGdAssignmentHealthByWorkload';
import {
    getAssignmentAvgCompletedTimeByWorkloadColumnDefs,
    tfGdAssignmentAvgCompletedTimeByWorkload,
} from '@/pages/OperationsInsights/composables/transformations/tfGdAssignmentAvgCompletedTimeByWorkload';
import {
    getAssignmentSummaryByWorkloadColumnDefs,
    tfGdAssignmentSummaryByWorkload,
} from '@/pages/OperationsInsights/composables/transformations/tfGdAssignmentSummaryByWorkload';
import { tfAssignmentSummariesData } from '@/pages/OperationsInsights/composables/transformations/tfAssignmentSummariesData';
import { AssignmentMetricsSummary } from '@/pages/OperationsInsights/types/AssignmentMetricsResponse';

const { taskDefinitionsMap } = useTaskDefinitionsStore();

type AssignmentsChartSeriesData = (
    | {
          type: 'column';
          name: string;
          yAxis: string;
          color: string;
          stack?: string;
          data: number[];
      }
    | {
          type: 'line';
          name: string;
          yAxis: string;
          color: string;
          data: number[];
      }
)[];

type AssignmentsCategoriesAndSeriesData = {
    xAxisCategories: string[];
    chartSeries: AssignmentsChartSeriesData;
};

export function useAssignmentsByWorkloadsData(queryParams: Ref<ApiQueryParams>) {
    const logger = useLogger('useWorkloadSnapshotsData');
    const assignmentMetricsSummary = ref<AssignmentMetricsSummary | null>(null);
    const enrichedAssignmentMetricsByWorkloadData = ref<EnrichedAssignmentMetricsByWorkload[] | null>(null);

    const isLoading = ref(false);
    const error = ref<string | null>(null);

    const assignmentSummariesData = computed(() => {
        if (!assignmentMetricsSummary.value) return {};

        return tfAssignmentSummariesData(assignmentMetricsSummary.value);
    });

    const assignmentsInOutChartSeries = computed((): AssignmentsCategoriesAndSeriesData => {
        if (!enrichedAssignmentMetricsByWorkloadData.value) {
            return { xAxisCategories: [], chartSeries: [] };
        }

        const categories = enrichedAssignmentMetricsByWorkloadData.value.map((workloadMetrics) => workloadMetrics.name);

        const chartSeries: AssignmentsChartSeriesData = [
            {
                type: 'column' as const,
                name: 'Created Assignments',
                yAxis: 'yaxis-left',
                color: '#1f77b4',
                stack: 'assignments',
                data: enrichedAssignmentMetricsByWorkloadData.value.map((m) => m.assignments),
            },
            {
                type: 'column' as const,
                name: 'Completed within SLA',
                yAxis: 'yaxis-left',
                color: '#2ca02c',
                stack: 'completed',
                data: enrichedAssignmentMetricsByWorkloadData.value.map((m) => m.completedWithinSLA),
            },
            {
                type: 'column' as const,
                name: 'Completed - SLA Breached',
                yAxis: 'yaxis-left',
                color: '#d62728',
                stack: 'completed',
                data: enrichedAssignmentMetricsByWorkloadData.value.map((m) => m.completedBreached),
            },
            {
                type: 'line' as const,
                name: 'Open Assignments',
                yAxis: 'yaxis-right',
                color: '#000000',
                data: enrichedAssignmentMetricsByWorkloadData.value.map((m) => m.openCountEoP),
            },
        ];

        return { xAxisCategories: categories, chartSeries };
    });

    // Grid column def and data for `Total Assignments By Workload`
    const totalAssignmentsByWorkloadColumnDefs = computed(() => {
        return getTotalAssignmentsByWorkloadColumnDefs();
    });

    const totalAssignmentsByWorkloadGridData = computed(() => {
        return enrichedAssignmentMetricsByWorkloadData.value ? tfGdAssignmentTotalByWorkload(enrichedAssignmentMetricsByWorkloadData.value) : [];
    });

    // High Chart - tfCsAssignmentOpenByWorkload
    const openAssignmentsByWorkloadChartSeries = computed(() => {
        return enrichedAssignmentMetricsByWorkloadData.value
            ? tfCsAssignmentOpenByWorkload(enrichedAssignmentMetricsByWorkloadData.value)
            : { xAxisCategories: [], chartSeries: [] };
    });

    // High Chart - tfCsAssignmentCompletionTimeByWorkload
    const assignmentsCompletionTimeByWorkloadChartSeries = computed(() => {
        return enrichedAssignmentMetricsByWorkloadData.value
            ? tfCsAssignmentCompletionTimeByWorkload(enrichedAssignmentMetricsByWorkloadData.value)
            : { xAxisCategories: [], chartSeries: [] };
    });

    // Grid column def and data for `Assignment Health by Workload`
    const assignmentHealthByWorkloadColumnDefs = computed(() => {
        return getAssignmentHealthByWorkloadColumnDefs();
    });

    const assignmentHealthByWorkloadGridData = computed(() => {
        return enrichedAssignmentMetricsByWorkloadData.value ? tfGdAssignmentHealthByWorkload(enrichedAssignmentMetricsByWorkloadData.value) : [];
    });

    // Grid column def and data for `Average Assignment Completion Time by Workload`
    const assignmentAvgCompletedTimeByWorkloadColumnDefs = computed(() => {
        return getAssignmentAvgCompletedTimeByWorkloadColumnDefs();
    });

    const assignmentAvgCompletedTimeByWorkloadGridData = computed(() => {
        return enrichedAssignmentMetricsByWorkloadData.value
            ? tfGdAssignmentAvgCompletedTimeByWorkload(enrichedAssignmentMetricsByWorkloadData.value)
            : [];
    });

    // Grid column def and data for `Assignment Summary by Workload`
    const assignmentSummaryByWorkloadColumnDefs = computed(() => {
        return getAssignmentSummaryByWorkloadColumnDefs();
    });

    const assignmentSummaryByWorkloadGridData = computed(() => {
        return enrichedAssignmentMetricsByWorkloadData.value ? tfGdAssignmentSummaryByWorkload(enrichedAssignmentMetricsByWorkloadData.value) : [];
    });

    const fetchData = async () => {
        if (!queryParams.value) return;
        if (!queryParams.value.workstreamDefinitionIds?.length) return;

        isLoading.value = true;
        error.value = null;

        try {
            const data = await fetchAssignmentsByWorkloads(queryParams.value);

            // Enrichment for assignments by workload data
            enrichedAssignmentMetricsByWorkloadData.value = data.assignmentMetricsByWorkloads
                ? data.assignmentMetricsByWorkloads.map((i) => {
                      const name = taskDefinitionsMap.get(i.workloadId)?.name ?? `Unknown (${i.workloadId})`;
                      const averageCompletionTimeInMinutes = i.completedCount
                          ? roundToOneDecimal(i.totalCompletionTimeSecs / i.completedCount / 60)
                          : 0;
                      return {
                          ...i,
                          name,
                          averageCompletionTimeInMinutes,
                      } as EnrichedAssignmentMetricsByWorkload;
                  })
                : null;

            assignmentMetricsSummary.value = data.summary ? data.summary : null;

            // TODO map from data.summary
        } catch (err) {
            error.value = 'Failed to fetch assignment by workload snapshots';
            logger.error('Failed to fetch assignment by workload snapshots', { error: err });
        } finally {
            isLoading.value = false;
        }
    };

    watch(queryParams, fetchData, { immediate: true, deep: true });

    return {
        assignmentSummariesData,
        assignmentsInOutChartSeries,
        totalAssignmentsByWorkloadColumnDefs,
        totalAssignmentsByWorkloadGridData,
        openAssignmentsByWorkloadChartSeries,
        assignmentsCompletionTimeByWorkloadChartSeries,
        assignmentHealthByWorkloadColumnDefs,
        assignmentHealthByWorkloadGridData,
        assignmentAvgCompletedTimeByWorkloadColumnDefs,
        assignmentAvgCompletedTimeByWorkloadGridData,
        assignmentSummaryByWorkloadColumnDefs,
        assignmentSummaryByWorkloadGridData,
        isLoading,
        error,
        fetchData,
    };
}
